<template>
  <Layout>
    <div v-if="showConfetti" class="confetti-overlay">
      <Vue3Lottie
        :animationData="confettiAnimation"
        :autoPlay="true"
        :loop="false"
        @onComplete="onConfettiComplete"
        ref="confettiRef"
      />
    </div>
    <div class="hero-section-fullwidth" v-if="!isLoading && product">
      <div class="hero-banner">
        <img
          :src="(product.images && product.images.length > 0) ? product.images[currentImageIndex] : defaultImage()"
          :alt="product.title"
          class="hero-background-image"
        />

        <div class="hero-overlay">
          <div class="hero-content-container">
            <nav class="breadcrumb">
              <router-link to="/marketplace">{{ $t('marketplace_name') }}</router-link>
              <span>›</span>
              <router-link
                v-if="product?.marketplaceCategory"
                :to="getCategoryMarketplaceLink()"
                class="category-link"
              >
                {{ product.marketplaceCategory.title }}
              </router-link>
              <span v-if="product?.marketplaceCategory">›</span>
              <span>{{ product?.title }}</span>
            </nav>

            <div class="hero-content">
              <h1 class="product-title">{{ product.title }}</h1>
              <p class="product-description">
                {{ truncatedDescription }}
                <span v-if="shouldShowSeeAll" class="see-all-link" @click="showDescriptionModal = true">...{{ $t('see_all') }}</span>
              </p>

              <div class="company-info-row">
                <div class="company-avatar">
                  <img v-if="product.companyInfo?.logo" :src="product.companyInfo?.logo" :alt="product.companyInfo?.name" />
                  <div v-else class="avatar-placeholder">
                    <span>{{ getCompanyInitials() }}</span>
                  </div>
                </div>
                <div class="company-details">
                  <span class="company-name">{{ product.companyInfo?.name}}</span>
                </div>
              </div>

              <div class="button-group">
                <button
                  class="purchase-btn-main"
                  :class="{ 'purchased': product.isPurchased === true, 'my-product': product.isMyProduct === true }"
                  @click="handlePurchaseClick"
                  :disabled="product.isPurchased === true || product.isMyProduct === true"
                >
                  <span v-if="product.isMyProduct === true">{{$t('your_product')}}</span>
                  <span v-else-if="product.isPurchased === true">{{$t('purchased')}}</span>
                  <span v-else-if="product.price === '0.00'">{{$t('buy_for_free_now')}}</span>
                  <span v-else>{{$t('buy_now')}} - {{ product.formattedPrice }}</span>
                </button>
                <button
                  v-if="product.isMyProduct === true"
                  class="icon-button"
                  @click="showDeleteModal"
                  :disabled="isDeleting"
                >
                  <img src="@/assets/images/trash.svg" alt="Delete" />
                </button>
              </div>
            </div>

            <!-- Stats Grid in Hero -->
            <div class="stats-grid-hero">
              <div class="stat-item-hero">
                <div class="stat-number-hero">{{ getUniqueModulesCount() }} {{ $t('module') }}</div>
                <div class="stat-label-hero">{{$t('module_amount_of_course')}}</div>
              </div>
              <div class="stat-item-hero">
                <div class="stat-number-hero">
                  <span class="rating-score-hero">{{ product.commentStats.averageRatingFormatted}}</span>
                  <div class="stars-hero">
                    <span v-for="i in 5" :key="i" :class="['star-hero', { 'filled': i <= Math.floor(product.commentStats.averageRating || 5) }]">★</span>
                  </div>
                </div>
                <div class="stat-label-hero">{{ product.commentStats.totalComments}} {{$t('user_points')}}</div>
              </div>
              <div class="stat-item-hero">
                <div class="stat-number-hero">{{ formatDateForStats(product.endDate) }}</div>
                <div class="stat-label-hero">{{$t('last_access_date')}}</div>
              </div>
              <div class="stat-item-hero">
                <span v-if="product.price === '0.00'" class="stat-number-hero">{{ $t('free') }}</span>
                <span class="stat-number-hero" v-else>{{ product.formattedPrice }}</span>
                <div class="stat-label-hero">{{$t('product_price_info')}}</div>
              </div>
            </div>
          </div>

          <template v-if="product.images.length > 1">
            <button class="nav-btn nav-prev" @click="prevImage" :disabled="currentImageIndex === 0">
              ‹
            </button>
            <button class="nav-btn nav-next" @click="nextImage" :disabled="currentImageIndex === product.images.length - 1">
              ›
            </button>
          </template>
        </div>
      </div>
    </div>

    <div class="marketplace-detail">
      <div class="detail-content" v-if="!isLoading && product">
        <div class="reviews-section" v-if="hasLearningObjectives()">
          <h2 class="section-title">{{$t('you_will_learn')}}</h2>
          <div class="learning-grid">
            <div class="learning-column">
              <div class="learning-item" v-for="(item, index) in getLeftColumnLearning()" :key="index">
                <svg class="check-icon" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                <span>{{ item }}</span>
              </div>
            </div>
            <div class="learning-column">
              <div class="learning-item" v-for="(item, index) in getRightColumnLearning()" :key="index">
                <svg class="check-icon" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                <span>{{ item }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="reviews-section" v-if="product.moduleInfo?.productInfo?.modules?.length > 0">
          <h2 class="section-title">{{$t('program_content')}}</h2>

          <div class="content-modules">
            <div v-for="(module, index) in getUniqueModules()" :key="`${module.programModuleId}-${index}`" class="module-accordion">
              <div class="module-header" @click="toggleModule(index)">
                <div class="module-left">
                  <span class="module-icon">{{ isModuleExpanded(index) ? '−' : '+' }}</span>
                  <div class="module-title-wrapper">
                    <span class="module-title">{{ module.name }}</span>
                    <span v-if="module.marketplaceInfo" class="module-marketplace-info">{{ module.marketplaceInfo }}</span>
                  </div>
                </div>
                <div class="module-right">
                  <span class="module-stats">{{ getVisibleSubModulesCount(module) }} {{ $t('sub_module') || 'Alt Modül' }}</span>
                </div>
              </div>

              <div v-show="isModuleExpanded(index)" class="module-content">
                <div v-if="getVisibleSubModules(module).length > 0">
                  <div v-for="subModule in getVisibleSubModules(module)" :key="subModule.programModuleId" class="submodule-item">
                    <div class="submodule-icon">
                      <svg viewBox="0 0 24 24">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                      </svg>
                    </div>
                    <div class="submodule-info">
                      <span class="submodule-title">{{ subModule.name }}</span>
                      <p v-if="subModule.marketplaceInfo" class="submodule-description">{{ subModule.marketplaceInfo }}</p>
                    </div>
                  </div>
                </div>
                <div v-else class="no-lessons">
                  <span class="empty-text">{{$t('couldnt_reach_product_info')}}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="product.commentStats?.comments.length > 0" class="reviews-section">
          <h2 class="section-title">{{ $t('comments') }}</h2>

          <div class="reviews-list">
            <div class="review-item" v-for="review in product.commentStats?.comments" :key="review.id">
              <div class="review-header">
                  <img class="reviewer-avatar" :src="review.companyInfo?.logo" :alt="review.companyInfo?.logo" />
                <div class="reviewer-info">
                  <div class="reviewer-name">{{ review.companyInfo?.name }}</div>
                  <div class="review-meta">
                    <div class="review-stars">
                      <span v-for="i in 5" :key="i" :class="['star', { 'filled': i <= review.rating }]">★</span>
                    </div>
                    <span class="review-date">{{ formatDate(review.createdAt) }}</span>
                  </div>
                </div>
              </div>
              <p class="review-text">{{ review.comment }}</p>
            </div>
          </div>

          <button class="show-more-reviews">{{$t('show_more')}}</button>
        </div>

        <div class="reviews-section">
          <h2 class="section-title">{{$t('discover_similar_contents')}}</h2>

          <div class="similar-products">
            <div @click="navigateProduct(similar.uuid)" class="similar-product" v-for="similar in similarProducts" :key="similar.id">
              <div class="similar-image">
                <img :src="(similar.images && similar.images.length > 0) ? similar.images[0] : defaultImage()" :alt="similar.title" />
                <div class="similar-overlay">
                  <span class="similar-new-badge">{{ $t('new') }}</span>
                </div>
              </div>
              <div class="similar-content">
                <div class="similar-top-row">
                  <h4 class="similar-title">{{ similar.title }}</h4>
                  <div class="similar-price">
                    <span v-if="similar.price === '0.00'">{{ $t('free') }}</span>
                    <span v-else>{{ similar.formattedPrice }}</span>
                  </div>
                </div>
                <p class="similar-description">{{ similar.description }}</p>
                <div class="similar-bottom-meta">
                  <span class="similar-category">{{ similar.marketplaceCategory?.title}}</span>
                  <div class="similar-rating">
                    <span class="rating-score">{{ similar.commentStats?.averageRatingFormatted || '5.0' }}</span>
                    <div class="stars">
                      <span v-for="i in 5" :key="i" :class="['star', { 'filled': i <= Math.floor(similar.commentStats?.averageRating || 5) }]">★</span>
                    </div>
                    <img src="../../src/assets/images/datepicker.svg" class="date-picker-class"/>
                    <span class="rating-date">Son:{{ formatDateForStats(similar.endDate) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- FAQ Section -->
        <div class="reviews-section">
          <h2 class="section-title">{{$t('frequently_asked_questions')}}</h2>

          <div class="faq-list">
            <div class="faq-item" v-for="(faq, index) in getFAQItems()" :key="index">
              <div class="faq-question" @click="toggleFAQ(index)">
                <span>{{ faq.question }}</span>
                <svg class="faq-icon" :class="{ 'expanded': isFAQExpanded(index) }" viewBox="0 0 24 24">
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </div>
              <div v-show="isFAQExpanded(index)" class="faq-answer">
                <p>{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LoadingOverlay v-if="isLoading" />
    </div>

    <PurchaseModal
      :isVisible="showPurchaseModal"
      :product="getPurchaseProductData()"
      @close="closePurchaseModal"
      @payment-success="onPaymentSuccess"
    />

    <!-- Description Modal -->
    <DescriptionModal
      :isVisible="showDescriptionModal"
      :description="product?.description"
      @close="showDescriptionModal = false"
    />

    <!-- Delete Confirmation Modal -->
    <ConfirmDeleteModal
      :isVisible="deleteModalVisible"
      :isLoading="isDeleting"
      :onAction="handleDeleteProduct"
      :onClose="closeDeleteModal"
    />
  </Layout>
</template>

<script>
import Layout from "@/components/layouts/Layout.vue";
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import PurchaseModal from "@/components/modals/PurchaseModal.vue";
import DescriptionModal from "@/components/modals/DescriptionModal.vue";
import ConfirmDeleteModal from "@/components/modals/ConfirmDeleteModal.vue";
import marketplaceService from "@/service/marketplaceService";
import { handleErrorResponse } from "@/utils/utils";
import {formatDate} from "@fullcalendar/core";
import defaultImage from '@/assets/images/ibility-default.jpeg';
import { Vue3Lottie } from 'vue3-lottie';
import confettiData from '@/assets/animations/confetti.json';


export default {
  name: 'MarketplaceDetails',
  components: {
    Layout,
    LoadingOverlay,
    PurchaseModal,
    DescriptionModal,
    ConfirmDeleteModal,
    Vue3Lottie
  },
  data() {
    return {
      isLoading: true,
      product: null,
      similarProducts: [],
      expandedModules: [],
      expandedFAQs: [],
      currentImageIndex: 0,
      allModulesExpanded: false,
      showPurchaseModal: false,
      showConfetti: false,
      confettiAnimation: confettiData,
      showDescriptionModal: false,
      deleteModalVisible: false,
      isDeleting: false
    }
  },
  computed: {
    isPurchased() {
      return this.product?.isPurchased ||
             this.product?.hasPurchased ||
             this.product?.userHasAccess ||
             !!this.product?.purchaseInfo;
    },
    isMyProduct() {
      return this.product?.isMyProduct === true;
    },
    truncatedDescription() {
      if (!this.product?.description) return '';
      const maxLength = 150;
      if (this.product.description.length <= maxLength) {
        return this.product.description;
      }
      return this.product.description.substring(0, maxLength);
    },
    shouldShowSeeAll() {
      if (!this.product?.description) return false;
      return this.product.description.length > 150;
    }
  },
  created() {
    this.loadProduct();
    this.loadSimilarProducts();
  },
      watch: {
      '$route.params.id': {
        handler(newId, oldId) {
          if (newId !== oldId) {
            this.isLoading = true;
            this.product = null;
            this.similarProducts = [];
            this.expandedModules = [];
            this.expandedFAQs = [];
            this.currentImageIndex = 0;
            this.showPurchaseModal = false;

            window.scrollTo(0, 0);
            this.loadProduct();
            this.loadSimilarProducts();
          }
        },
        immediate: false
      }
    },

  methods: {
    defaultImage() {
      return defaultImage
    },
    formatDate,

    async loadProduct() {
      try {
        this.isLoading = true;
        const productId = this.$route.params.id;
        const response = await marketplaceService.getFromId(productId);
        this.product = response.data.data;

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.$router.push('/marketplace');
      } finally {
        this.isLoading = false;
      }
    },

    async loadSimilarProducts(){
      try {
        const response = await marketplaceService.getSimilarProducts(this.$route.params.id);
        this.similarProducts = response.data.data;
      } catch (error) {
        this.similarProducts = this.getStaticSimilarProducts();
      }
    },
    getCompanyInitials() {
      if (this.product?.companyInfo?.name) {
        return this.product.companyInfo.name.substring(0, 2).toUpperCase();
      }
      return 'iB';
    },
    navigateProduct(uuid) {
      this.$router.push(`/marketplace/product/${uuid}`);
    },

    formatDateForStats(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },


    hasLearningObjectives() {
      return this.product?.topicsToLearn && this.product.topicsToLearn.length > 0;
    },

    getLeftColumnLearning() {
      const objectives = this.getLearningObjectives();
      const half = Math.ceil(objectives.length / 2);
      return objectives.slice(0, half);
    },

    getRightColumnLearning() {
      const objectives = this.getLearningObjectives();
      const half = Math.ceil(objectives.length / 2);
      return objectives.slice(half);
    },

    getLearningObjectives() {
      const objectives = [];

      if (this.product?.topicsToLearn && Array.isArray(this.product.topicsToLearn)) {
        objectives.push(...this.product.topicsToLearn);
      }

      return objectives;
    },


    getFAQItems() {
      return [
        {
          question: this.$t('faq_q_1'),
          answer: this.$t('faq_a_1'),
        },
        {
          question: this.$t('faq_q_2'),
          answer: this.$t('faq_a_2'),
        },
        {
          question: this.$t('faq_q_3'),
          answer: this.$t('faq_a_3'),
        },
        {
          question: this.$t('faq_q_4'),
          answer: this.$t('faq_a_4'),
        },
        {
          question: this.$t('faq_q_5'),
          answer: this.$t('faq_a_5'),
        },
        {
          question: this.$t('faq_q_6'),
          answer: this.$t('faq_a_6'),
        }
      ];
    },
    toggleFAQ(index) {
      const idx = this.expandedFAQs.indexOf(index);
      if (idx > -1) {
        this.expandedFAQs.splice(idx, 1);
      } else {
        this.expandedFAQs.push(index);
      }
    },

    isFAQExpanded(index) {
      return this.expandedFAQs.includes(index);
    },

    getUniqueModules() {
      if (!this.product?.moduleInfo?.productInfo?.modules) return [];
      const seen = new Set();
      return this.product.moduleInfo.productInfo.modules.filter(module => {
        if (seen.has(module.programModuleId)) {
          return false;
        }
        seen.add(module.programModuleId);
        return true;
      });
    },

    getUniqueModulesCount() {
      return this.getUniqueModules().length;
    },

    getVisibleSubModules(module) {
      if (!module?.subModules || !Array.isArray(module.subModules)) return [];
      return module.subModules.filter(subModule => subModule.marketplaceIsHidden !== true);
    },

    getVisibleSubModulesCount(module) {
      return this.getVisibleSubModules(module).length;
    },

    toggleModule(index) {
      const idx = this.expandedModules.indexOf(index);
      if (idx > -1) {
        this.expandedModules.splice(idx, 1);
      } else {
        this.expandedModules.push(index);
      }
      this.allModulesExpanded = this.expandedModules.length === this.getUniqueModules().length;
    },

    isModuleExpanded(index) {
      return this.expandedModules.includes(index);
    },


    handlePurchaseClick() {
      if (!(this.product?.isPurchased === true) && !(this.product?.isMyProduct === true)) {
        this.purchaseProduct();
      }
    },

    purchaseProduct() {
      this.showPurchaseModal = true;
    },

    closePurchaseModal() {
      this.showPurchaseModal = false;
    },

        onPaymentSuccess(paymentData) {
      this.showPurchaseModal = false;

      this.showConfetti = true;

      this.$snackbar.add({
        type: 'success',
        text: this.$t('payment_successful')
      });

      this.loadProduct();
    },

    onConfettiComplete() {
      this.showConfetti = false;
    },

    getPurchaseProductData() {
      if (!this.product) return {};

      return {
        uuid: this.product.uuid,
        title: this.product.title,
        price: this.product.price,
        formattedPrice: this.product.formattedPrice,
        exchangeRate: this.product.exchangeRate || null,
        image: (this.product.images && this.product.images.length > 0) ? this.product.images[0] : this.defaultImage(),
        companyName: this.product.companyInfo?.name || 'iBility'
      };
    },


    nextImage() {
      if (this.currentImageIndex < this.product.images.length - 1) {
        this.currentImageIndex++;
      }
    },

    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
      }
    },

    getCategoryMarketplaceLink() {
      if (!this.product?.marketplaceCategory?.uuid) {
        return '/marketplace';
      }

      return {
        path: '/marketplace',
        query: {
          marketplaceCategoryId: this.product.marketplaceCategory.uuid
        }
      };
    },

    showDeleteModal() {
      this.deleteModalVisible = true;
    },

    closeDeleteModal() {
      this.deleteModalVisible = false;
    },

    async handleDeleteProduct() {
      if (!this.product?.uuid) return;

      try {
        this.isDeleting = true;
        await marketplaceService.delete(this.product.uuid);

        this.$snackbar.add({
          type: 'success',
          text: this.$t('marketplace_product_deleted')
        });

        this.deleteModalVisible = false;
        this.$router.push('/marketplace');
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      } finally {
        this.isDeleting = false;
      }
    }

  }
}
</script>

<style scoped>
.marketplace-detail {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  font-family: 'euclid_medium', sans-serif;
}

/* Breadcrumb */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 32px;
  padding: 0;
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  transition: color 0.3s ease;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.breadcrumb a:hover {
  color: white;
  text-decoration: underline;
}
.date-picker-class{
  width: 16px;
  height: 16px;
  margin-left: 4px;
}
.breadcrumb span {
  color: rgba(255, 255, 255, 0.6);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.category-link {
  color: rgba(255, 255, 255, 0.9) !important;
  text-decoration: none;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* Header Section */
.hero-section-fullwidth {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-bottom: 48px;
  position: relative;
  z-index: 10;
}

.hero-banner {
  position: relative;
  width: 100%;
  min-height: 600px;
  border-radius: 0;
  overflow: visible;
  display: flex;
  align-items: center;
  z-index: 1;
}

.hero-background-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.7) 0%,
    rgba(28, 60, 213, 0.5) 50%,
    rgba(0, 0, 0, 0.6) 100%
  );
  display: flex;
  align-items: center;
  z-index: 2;
}

.hero-content-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 20px;
  width: 100%;
  position: relative;
  z-index: 3;
}

.hero-content {
  max-width: 600px;
  color: white;
  z-index: 4;
  position: relative;
}

.product-title {
  font-family: 'euclid_medium', sans-serif;
  font-size: 40px;
  line-height: 1.2;
  color: white;
  margin: 0 0 16px 0;
}

.product-description {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 32px 0;
}

.company-info-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  border-radius: 12px;
}

.company-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.company-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.company-details {
  display: flex;
  flex-direction: column;
}

.company-name {
  font-size: 20px;
  font-weight: 600;
  color: white;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.8);
}

.course-count {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.8);
}

.purchase-btn-main {
    background: #1C3CD5;
    border-radius: 16px;
    padding: 10px 30px;
    color: #fff;
    font-size: 12px;
    font-family: 'euclid_medium', sans-serif;
    cursor: pointer;
    border: none;
    outline: none;
    transition: all 0.1s ease-in;
}

.purchase-btn-main.purchased {
  background: #10b981;
  color: white;
  cursor: default;
}

.purchase-btn-main.purchased:hover {
  background: #10b981;
  transform: none;
}

.purchase-btn-main.my-product {
  background: #1C3CD5;
  color: white;
  cursor: default;
}

.purchase-btn-main.my-product:hover {
  background: #1C3CD5;
  transform: none;
}

.button-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.delete-btn-main img {
  height: 16px;
  width: 16px;
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.nav-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-50%) scale(1.1);
}

.nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-prev {
  left: 24px;
}

.nav-next {
  right: 24px;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 48px;
  padding: 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-item {
  text-align: center;
  padding: 16px;
}

.stat-number {
  font-size: 20px;
  font-weight: 700;
  color: #1c1d1f;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.rating-score {
  font-weight: 700;
  color: #1c1d1f;
}

.stars {
  display: flex;
  gap: 2px;
}

.star {
  color: #e59819;
  font-size: 16px;
}

.star:not(.filled) {
  color: #d1d7dc;
}

.section-title {
  font-family: 'euclid_medium', sans-serif;
  font-size: 20px;
  color: #101828;
  margin: 0 0 24px 0;
  border-bottom: 1px solid #EAECF0;
  padding-bottom: 12px;
}

.learning-section {
  margin-bottom: 48px;
}

.learning-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

.learning-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.learning-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.check-icon {
  width: 20px;
  height: 20px;
  fill: #10B981;
  flex-shrink: 0;
  margin-top: 2px;
}

.learning-item span {
  font-size: 16px;
  line-height: 1.5;
  color: #1c1d1f;
}

.program-content-section {
  margin-bottom: 48px;
}

.content-modules {
  background: white;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  overflow: hidden;
}

.module-accordion {
  border-bottom: 1px solid #E5E7EB;
}

.module-accordion:last-child {
  border-bottom: none;
}

.module-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  cursor: pointer;
  background: #F9FAFB;
  transition: all 0.3s ease;
}

.module-header:hover {
  background: #F3F4F6;
}

.module-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.module-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #6B7280;
  transition: all 0.3s ease;
}

.module-title-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.module-title {
  font-size: 16px;
  font-weight: 600;
  color: #1c1d1f;
}

.module-marketplace-info {
  font-size: 12px;
  color: #6B7280;
  font-weight: 400;
}

.module-stats {
  font-size: 14px;
  color: #6a6f73;
}

/* SubModule Styles */
.submodule-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid #F3F4F6;
}

.submodule-item:last-child {
  border-bottom: none;
}

.submodule-icon {
  width: 24px;
  height: 24px;
  color: #1C3CD5;
  flex-shrink: 0;
  margin-top: 2px;
}

.submodule-icon svg {
  width: 100%;
  height: 100%;
  fill: currentColor;
}

.submodule-info {
  flex: 1;
}

.submodule-title {
  font-size: 15px;
  font-weight: 500;
  color: #1c1d1f;
  display: block;
  margin-bottom: 4px;
}

.submodule-description {
  font-size: 13px;
  color: #6a6f73;
  line-height: 1.5;
  margin: 0;
}

.module-content {
  padding: 0 24px 16px;
  background: white;
}

.lesson-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #F3F4F6;
}

.lesson-item:last-child {
  border-bottom: none;
}

.lesson-icon {
  width: 20px;
  height: 20px;
  color: #6B7280;
}

.lesson-icon svg {
  width: 100%;
  height: 100%;
  fill: currentColor;
}

.lesson-info {
  flex: 1;
}

.lesson-title {
  font-size: 14px;
  color: #1c1d1f;
}

.lesson-duration {
  font-size: 12px;
  color: #6a6f73;
}

.no-lessons {
  padding: 24px;
  text-align: center;
  color: #6a6f73;
}

/* Reviews Section */
.reviews-section {
  margin-bottom: 48px;
  border: 1px solid #EAECF0;
  border-radius: 12px;
  padding: 24px;
}

.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
}

.review-item {
  background: white;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
}

.review-header {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #6B7280;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.reviewer-info {
  flex: 1;
}

.reviewer-name {
  font-size: 16px;
  font-weight: 600;
  color: #1c1d1f;
  margin-bottom: 4px;
}

.review-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.review-stars {
  display: flex;
  gap: 2px;
}

.review-stars .star {
  font-size: 14px;
}

.review-date {
  font-size: 12px;
  color: #6a6f73;
}

.review-text {
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  margin: 0;
}

.show-more-reviews {
  background: none;
  border: 1px solid #D1D5DB;
  color: #374151;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  margin: 0 auto;
}

.show-more-reviews:hover {
  background: #F9FAFB;
  border-color: #9CA3AF;
}


.similar-products {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.similar-product {
  display: flex;
  cursor: pointer;
}


.similar-image {
  position: relative;
  width: 280px;
  height: 180px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 12px;
}

.similar-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.similar-overlay {
  position: absolute;
  top: 12px;
  left: 12px;
}

.similar-new-badge {
  background: #ef4444;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-family: 'euclid_medium', sans-serif;
}

.similar-content {
  padding: 0px 20px 20px 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.similar-top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.similar-price {
  color: #101828;
  font-size: 16px;
  font-weight: 600;
  font-family: 'euclid_medium', sans-serif;
}

.similar-bottom-meta {
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  margin-top: auto;
}

.similar-category {
  color: #6B7280;
  font-size: 12px;
  font-weight: 500;
}

.similar-rating {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.rating-score {
  font-weight: 600;
  color: #1c1d1f;
  font-size: 14px;
}

.similar-rating .stars {
  display: flex;
  gap: 1px;
}

.similar-rating .star {
  font-size: 14px;
  color: #e59819;
}

.similar-rating .star:not(.filled) {
  color: #d1d7dc;
}

.rating-date {
  font-size: 12px;
  color: #6a6f73;
}

.similar-title {
  font-size: 18px;
  color: #1c1d1f;
  font-weight: 500;
  font-family: 'euclid_regular', sans-serif;
}

.similar-description {
  font-size: 14px;
  color: #667085;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 0;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: 'euclid_regular', sans-serif;
  font-weight: 400;
}

/* FAQ Section */
.faq-section {
  margin-bottom: 48px;
}

.faq-list {
  background: white;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  overflow: hidden;
}

.faq-item {
  border-bottom: 1px solid #E5E7EB;
}

.faq-item:last-child {
  border-bottom: none;
}

.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  cursor: pointer;
  background: #F9FAFB;
  transition: all 0.3s ease;
}

.faq-question:hover {
  background: #F3F4F6;
}

.faq-question span {
  font-size: 16px;
  font-weight: 500;
  color: #1c1d1f;
  flex: 1;
  margin-right: 16px;
}

.faq-icon {
  width: 20px;
  height: 20px;
  fill: #6B7280;
  transition: transform 0.3s ease;
}

.faq-icon.expanded {
  transform: rotate(180deg);
}

.faq-answer {
  padding: 0 24px 20px;
  background: white;
}

.faq-answer p {
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  padding-top: 12px;
}

.stats-grid-hero {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 20px;
  padding: 15px;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(0, 0, 0, 0.3);
  position: relative;
  bottom: -15%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100 !important;
  width: calc(100% - 0px);
  max-width: 1200px;
}

.stat-item-hero {
  text-align: center;
  border-right: 1px solid #E5E7EB;
}

.stat-item-hero:last-child {
  border-right: none;
}

.stat-number-hero {
  font-size: 16px;
  font-weight: 700;
  color: #1c1d1f;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.stat-label-hero {
  font-size: 12px;
  color: #6a6f73;
  line-height: 1.4;
}

.rating-score-hero {
  font-weight: 700;
  color: #1c1d1f;
}

.stars-hero {
  display: flex;
  gap: 2px;
}

.star-hero {
  color: #e59819;
  font-size: 16px;
}

.star-hero:not(.filled) {
  color: #d1d7dc;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .hero-banner {
    min-height: 500px;
  }

  .hero-content-container {
    padding: 32px 20px;
  }

  .product-title {
    font-size: 32px;
  }

  .product-description {
    font-size: 16px;
  }

  .stats-grid-hero {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    padding: 20px;
    width: calc(100% - 40px);
    bottom: -40px;
  }

  .learning-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .similar-product {
    flex-direction: column;
  }

  .similar-image {
    width: 100%;
    height: 200px;
  }

  .similar-content {
    padding: 16px;
  }

  .similar-title {
    font-size: 16px;
  }
}

@media (max-width: 768px) {
  .marketplace-detail {
    padding: 0 16px;
  }

  .hero-banner {
    min-height: 450px;
  }

  .hero-content-container {
    padding: 24px 16px;
  }

  .hero-content {
    max-width: 100%;
  }

  .product-title {
    font-size: 24px;
    margin-bottom: 12px;
  }

  .product-description {
    font-size: 14px;
    margin-bottom: 24px;
  }

  .company-info-row {
    padding: 12px 16px;
    margin-bottom: 24px;
  }

  .company-avatar {
    width: 40px;
    height: 40px;
  }

  .company-name {
    font-size: 16px;
  }

  .purchase-btn-main {
    padding: 14px 28px;
    font-size: 16px;
  }

  .stats-grid-hero {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 16px;
    width: calc(100% - 32px);
    bottom: -30px;
  }


  .stat-number-hero {
    font-size: 18px;
  }

  .nav-btn {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .nav-prev {
    left: 16px;
  }

  .nav-next {
    right: 16px;
  }

  .section-title {
    font-size: 20px;
  }

  .module-header {
    padding: 16px;
  }

  .module-content {
    padding: 0 16px 12px;
  }

  .review-item {
    padding: 16px;
  }

  .similar-content {
    padding: 16px;
  }

  .faq-question {
    padding: 16px;
  }

  .faq-answer {
    padding: 0 16px 16px;
  }
}

/* Full Screen Confetti */
.confetti-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  z-index: 999999 !important;
  background: rgba(0, 0, 0, 0.1) !important;
  pointer-events: none !important;
}

.confetti-overlay .lottie-container,
.confetti-overlay > div {
  max-width: 100vw;
  max-height: 100vh;
  width: 100%;
  height: 100%;
}

.see-all-link {
  color: #ffffff;
  cursor: pointer;
  font-family: euclid_semi_bold, sans-serif;
  transition: color 0.2s ease;
}

.see-all-link:hover {
  color: #1c3cd5;
  text-decoration: underline;
}
</style>
